import React, { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../components/Footer';
import { ArrowRight, ArrowLeft, CheckCircle, Calculator } from 'lucide-react';
import { motion } from 'framer-motion';

const detailedServices = [
  {
    title: 'Renovations',
    description: 'Expert renovation services for residential and commercial properties.',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Complete renovations',
      'Interior updates',
      'Exterior improvements',
      'Historic renovations',
      'Property upgrades',
      'Modern updates'
    ],
    process: ['Assessment', 'Design', 'Planning', 'Renovation', 'Completion'],
    path: '/services/renovations'
  },
  {
    title: 'Bathroom Remodeling',
    description: 'Transform your bathroom into a beautiful retreat with our remodeling services.',
    image: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Custom vanities',
      'Quality tile work',
      'Modern fixtures',
      'Spa-inspired designs',
      'Heated flooring',
      'Smart technology'
    ],
    process: ['Design', 'Material Selection', 'Demolition', 'Installation', 'Finishing'],
    path: '/services/bathroom-remodeling-westchester'
  },
  {
    title: 'Kitchen Renovation',
    description: 'Create a stunning kitchen that combines functionality with style.',
    image: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Custom cabinets',
      'Quality appliances',
      'Countertop installation',
      'Designer lighting',
      'Smart integration',
      'Storage solutions'
    ],
    process: ['Design', 'Planning', 'Material Selection', 'Installation', 'Completion'],
    path: '/services/kitchen-renovation-westchester'
  },
  {
    title: 'New Construction',
    description: 'Full-service new construction for residential and commercial projects.',
    image: 'https://images.unsplash.com/photo-1541976590-713941681591?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Custom home building',
      'Commercial construction',
      'Project management',
      'Permit handling',
      'Quality assurance',
      'Sustainable building'
    ],
    process: ['Planning', 'Design', 'Permitting', 'Construction', 'Completion'],
    path: '/services/new-construction'
  },
  {
    title: 'Commercial Construction',
    description: 'Expert commercial construction and renovation services.',
    image: 'https://images.unsplash.com/photo-1565008447742-97f6f38c985c?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Office buildings',
      'Retail spaces',
      'Industrial facilities',
      'Medical offices',
      'Restaurants',
      'Mixed-use developments'
    ],
    process: ['Consultation', 'Planning', 'Design', 'Construction', 'Delivery'],
    path: '/services/commercial-construction'
  },
  {
    title: 'Construction Management',
    description: 'Comprehensive construction management services.',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Project oversight',
      'Schedule management',
      'Cost control',
      'Quality assurance',
      'Safety compliance',
      'Vendor coordination'
    ],
    process: ['Planning', 'Execution', 'Monitoring', 'Control', 'Completion'],
    path: '/services/construction-management'
  },
  {
    title: 'Design-Build Services',
    description: 'Integrated design and construction services.',
    image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Architectural design',
      'Engineering',
      'Construction',
      'Project management',
      'Cost control',
      'Single-source responsibility'
    ],
    process: ['Design', 'Engineering', 'Planning', 'Construction', 'Delivery'],
    path: '/services/design-build'
  },
  {
    title: 'Home Additions',
    description: 'Expand your living space with additions that seamlessly integrate with your home.',
    image: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Room additions',
      'Second stories',
      'Garage conversions',
      'In-law suites',
      'Sunrooms',
      'Master suites'
    ],
    process: ['Planning', 'Design', 'Permitting', 'Construction', 'Finishing'],
    path: '/services/home-additions-westchester'
  },
  {
    title: 'Custom Wine Cellars',
    description: 'Design and build the perfect environment for your wine collection.',
    image: 'https://images.unsplash.com/photo-1611575189074-9dfbbceb258a?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Climate control',
      'Custom racking',
      'LED lighting',
      'Security systems',
      'Tasting areas',
      'Smart controls'
    ],
    process: ['Design', 'Climate Planning', 'Construction', 'Installation', 'Testing'],
    path: '/services/wine-cellars-westchester'
  },
  {
    title: 'Pool & Spa Design',
    description: 'Create your personal oasis with our custom pool and spa designs.',
    image: 'https://images.unsplash.com/photo-1572331165267-854da2b10ccc?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Custom pools',
      'Infinity edges',
      'Smart controls',
      'LED lighting',
      'Water features',
      'Spa integration'
    ],
    process: ['Design', 'Planning', 'Construction', 'Installation', 'Finishing'],
    path: '/services/pool-spa-design-westchester'
  },
  {
    title: 'Home Theater',
    description: 'Experience cinema-quality entertainment in your own home.',
    image: 'https://images.unsplash.com/photo-1593784991095-a205069470b6?auto=format&fit=crop&q=80&w=1200',
    features: [
      'Acoustic treatment',
      'Projection systems',
      'Sound systems',
      'Custom seating',
      'Smart lighting',
      'Control systems'
    ],
    process: ['Design', 'Acoustic Planning', 'Installation', 'Calibration', 'Testing'],
    path: '/services/home-theater-westchester'
  }
];

const ServiceCard = lazy(() => import('../components/ServiceCard'));

export function ServicesPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Construction Services | Licensed in Westchester NY & Fairfield CT</title>
        <meta name="description" content="Comprehensive construction services in Westchester County, NY and Fairfield County, CT. New construction, commercial projects, renovations, and construction management. Licensed and insured in both states." />
        <meta name="keywords" content="construction services Westchester, construction services Fairfield, commercial construction NY CT, new construction Westchester, construction management Fairfield" />
        <link rel="canonical" href="https://renovationwestchester.com/services" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Construction Services | Westchester NY & Fairfield CT" />
        <meta property="og:description" content="Expert construction services in Westchester and Fairfield counties. Licensed and insured in both NY and CT." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://renovationwestchester.com/services" />
      </Helmet>
      
      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Home</span>
          </Link>

          <div className="mb-16">
            <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">Construction Services</h1>
            <p className="text-zinc-400 max-w-2xl leading-relaxed">
              Licensed and insured in both Westchester County, NY and Fairfield County, CT. 
              Our comprehensive construction services cover everything from new construction 
              to renovations, with a focus on quality and professionalism.
            </p>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16 relative group"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 rounded-xl blur-lg opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
            <Link
              to="/project-planner"
              className="relative flex items-center gap-4 bg-gradient-to-r from-zinc-900 to-zinc-800 p-8 rounded-xl border border-yellow-400/20 hover:border-yellow-400/40 transition-all duration-300"
            >
              <div className="p-4 bg-yellow-400/10 rounded-lg">
                <Calculator className="h-12 w-12 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl text-zinc-100 mb-2">Start Your Project Today</h3>
                <p className="text-zinc-400">Get a free consultation for your construction project</p>
              </div>
              <ArrowRight className="h-6 w-6 text-yellow-400 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>

          <div className="space-y-32">
            <Suspense fallback={<div className="animate-pulse bg-zinc-800 h-96"></div>}>
              {detailedServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className="grid md:grid-cols-2 gap-16 items-center"
                >
                  <div className={`${index % 2 === 1 ? 'md:order-2' : ''}`}>
                    <h2 className="text-3xl font-light text-zinc-100 mb-6">{service.title}</h2>
                    <p className="text-zinc-400 mb-8 leading-relaxed">{service.description}</p>
                    
                    <div className="mb-8">
                      <h3 className="text-xl text-zinc-200 mb-4">Features</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {service.features.map((feature, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                            <span className="text-zinc-400">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mb-8">
                      <h3 className="text-xl text-zinc-200 mb-4">Our Process</h3>
                      <div className="flex flex-wrap gap-4">
                        {service.process.map((step, i) => (
                          <div key={i} className="flex items-center gap-2 bg-zinc-800/50 px-4 py-2 rounded">
                            <span className="text-yellow-400">{i + 1}.</span>
                            <span className="text-zinc-400">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Link 
                      to={service.path}
                      className="inline-flex items-center gap-2 text-yellow-400 hover:gap-4 transition-all duration-300"
                    >
                      <span className="text-sm tracking-wider">Learn More</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>

                  <Link 
                    to={service.path}
                    className={`aspect-[4/3] overflow-hidden ${index % 2 === 1 ? 'md:order-1' : ''}`}
                  >
                    <img 
                      src={service.image} 
                      alt={service.title}
                      loading="lazy"
                      width="1200"
                      height="900"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                    />
                  </Link>
                </motion.div>
              ))}
            </Suspense>
          </div>

          <div className="mt-32 bg-zinc-800/30 p-12 rounded-xl">
            <h2 className="text-3xl font-light text-zinc-100 mb-8 text-center">Complete Construction & Renovation Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div>
                <h3 className="text-xl text-zinc-100 mb-4">Renovation Services</h3>
                <ul className="space-y-2 text-zinc-400">
                  <li>• Complete home renovations</li>
                  <li>• Kitchen renovations</li>
                  <li>• Bathroom remodeling</li>
                  <li>• Interior renovations</li>
                  <li>• Exterior improvements</li>
                  <li>• Historic renovations</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl text-zinc-100 mb-4">Construction Services</h3>
                <ul className="space-y-2 text-zinc-400">
                  <li>• New construction</li>
                  <li>• Commercial construction</li>
                  <li>• Home additions</li>
                  <li>• Basement finishing</li>
                  <li>• Deck & patio construction</li>
                  <li>• Custom carpentry</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl text-zinc-100 mb-4">Specialized Services</h3>
                <ul className="space-y-2 text-zinc-400">
                  <li>• Custom wine cellars</li>
                  <li>• Home theaters</li>
                  <li>• Pool & spa design</li>
                  <li>• Design-build services</li>
                  <li>• Construction management</li>
                  <li>• General repairs</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl text-zinc-100 mb-4">Interior Services</h3>
                <ul className="space-y-2 text-zinc-400">
                  <li>• Interior design</li>
                  <li>• Flooring installation</li>
                  <li>• Painting services</li>
                  <li>• Tile work</li>
                  <li>• Countertop installation</li>
                  <li>• Drywall services</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl text-zinc-100 mb-4">Systems & Infrastructure</h3>
                <ul className="space-y-2 text-zinc-400">
                  <li>• Electrical work</li>
                  <li>• Plumbing services</li>
                  <li>• HVAC installation</li>
                  <li>• Roofing services</li>
                  <li>• Window installation</li>
                  <li>• Insulation services</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl text-zinc-100 mb-4">Additional Services</h3>
                <ul className="space-y-2 text-zinc-400">
                  <li>• Door installation</li>
                  <li>• Sustainable building</li>
                  <li>• Historic restoration</li>
                  <li>• Emergency services</li>
                  <li>• Project consultation</li>
                  <li>• Permit assistance</li>
                </ul>
              </div>
            </div>
            
            <div className="text-center border-t border-zinc-700 pt-8">
              <h3 className="text-2xl font-light text-zinc-100 mb-4">Licensed & Insured in NY & CT</h3>
              <p className="text-zinc-400 mb-8 max-w-3xl mx-auto">
                Serving Westchester County, NY and Fairfield County, CT with comprehensive construction and renovation services. 
                From small repairs to major construction projects, we deliver quality craftsmanship and professional service.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link 
                  to="/consultation"
                  className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900 group transform hover:scale-105 transition-all duration-300"
                >
                  <span className="text-sm tracking-wider">Get Free Consultation</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <a 
                  href="tel:9149606433"
                  className="inline-flex items-center gap-4 px-8 py-4 border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-zinc-900 transition-all duration-300"
                >
                  <span className="text-sm tracking-wider">Call (914) 960-6433</span>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-32 bg-zinc-800/30 p-12 rounded-xl text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">We Do Many More Services</h2>
            <p className="text-zinc-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              Beyond our featured services, we offer a comprehensive range of construction and renovation solutions. 
              From small repairs to major construction projects, our experienced team handles it all with the same 
              commitment to quality and professionalism. Contact us to discuss your specific needs.
            </p>
            <div className="mb-8">
              <h3 className="text-xl text-zinc-100 mb-4">Serving Westchester County, NY & Fairfield County, CT</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
                <div className="text-zinc-400 text-sm">Scarsdale</div>
                <div className="text-zinc-400 text-sm">Greenwich</div>
                <div className="text-zinc-400 text-sm">Rye</div>
                <div className="text-zinc-400 text-sm">Harrison</div>
                <div className="text-zinc-400 text-sm">Mamaroneck</div>
                <div className="text-zinc-400 text-sm">Bedford</div>
                <div className="text-zinc-400 text-sm">Chappaqua</div>
                <div className="text-zinc-400 text-sm">Bronxville</div>
                <div className="text-zinc-400 text-sm">Larchmont</div>
                <div className="text-zinc-400 text-sm">Purchase</div>
                <div className="text-zinc-400 text-sm">White Plains</div>
                <div className="text-zinc-400 text-sm">New Rochelle</div>
                <div className="text-zinc-400 text-sm">Stamford</div>
                <div className="text-zinc-400 text-sm">Norwalk</div>
                <div className="text-zinc-400 text-sm">Westport</div>
                <div className="text-zinc-400 text-sm">Darien</div>
                <div className="text-zinc-400 text-sm">New Canaan</div>
                <div className="text-zinc-400 text-sm">Wilton</div>
                <div className="text-zinc-400 text-sm">Ridgefield</div>
                <div className="text-zinc-400 text-sm">Fairfield</div>
                <div className="text-zinc-400 text-sm">Trumbull</div>
                <div className="text-zinc-400 text-sm">Easton</div>
                <div className="text-zinc-400 text-sm">Weston</div>
                <div className="text-zinc-400 text-sm">Redding</div>
              </div>
            </div>
            <Link 
              to="/consultation"
              className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900 group transform hover:scale-105 transition-all duration-300"
            >
              <span className="text-sm tracking-wider">Contact Us Today</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}